import React, { useState, useEffect } from 'react';
import { api, listWeddingImages } from '../../lib/api';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import MediaGrid from './MediaGrid';
import Button from '../ui/Button';
import Icon from '../AppIcon';

const MediaLibraryModal = ({ isOpen, onClose, onSelectImage, weddingId }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUrl, setSelectedUrl] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const { token } = useAdminAuth();

  const loadImages = async (cursor = null) => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const result = weddingId
        ? await listWeddingImages(token, weddingId, 50, cursor)
        : await api.listImages(token, 50, cursor);
      if (cursor) {
        setImages((prev) => [...prev, ...result.images]);
      } else {
        setImages(result.images);
      }
      setNextCursor(result.nextCursor);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && token) {
      loadImages();
      setSelectedUrl(null);
    }
  }, [isOpen, token]);

  const handleSelectImage = (url) => {
    setSelectedUrl(url);
  };

  const handleConfirmSelection = () => {
    if (selectedUrl && onSelectImage) {
      onSelectImage(selectedUrl);
      onClose();
    }
  };

  const handleCopyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      alert('Image URL copied to clipboard!');
    } catch (err) {
      alert('Failed to copy URL');
    }
  };

  const handleLoadMore = () => {
    if (nextCursor) {
      loadImages(nextCursor);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold">Media Library</h2>
            <p className="text-sm text-gray-600 mt-1">
              Select an image from your uploaded media
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            title="Close"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
              {error}
            </div>
          )}

          <MediaGrid
            images={images}
            onSelectImage={handleSelectImage}
            selectedUrl={selectedUrl}
            loading={loading}
          />

          {/* Load more */}
          {nextCursor && !loading && (
            <div className="mt-6 text-center">
              <Button onClick={handleLoadMore} variant="outline">
                Load More Images
              </Button>
            </div>
          )}

          {loading && images.length > 0 && (
            <div className="mt-6 text-center text-gray-500">
              Loading more images...
            </div>
          )}

          {images.length === 0 && !loading && (
            <div className="text-center py-12 text-gray-500">
              <p className="mb-2">No images in your media library yet.</p>
              <p className="text-sm">Upload images in the Media tab first.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <div className="text-sm text-gray-600">
            {selectedUrl ? (
              <div className="flex items-center gap-2">
                <Icon name="Check" size={16} className="text-primary" />
                <span>Image selected</span>
                <button
                  onClick={() => handleCopyUrl(selectedUrl)}
                  className="ml-2 text-primary hover:underline"
                >
                  Copy URL
                </button>
              </div>
            ) : (
              <span>Select an image to continue</span>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSelection}
              disabled={!selectedUrl}
            >
              Use Selected Image
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaLibraryModal;
